import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Search } from 'lucide-react';
import Layout from './layout';
import { campaignData } from '../data/mockData';
import CampaignManagementModal from '../Modal/CampaignManagementModal';
import EditCampaignManagementModal from '../Modal/EditCampaignManagementModal';
import { campaignAPI } from '../services/api';

// Styled Components
const PageContainer = styled.div`
  background-color: #FFFFFF;
  border-radius: 0.5rem;
  border: 1px solid #E5E7EB;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SearchContainer = styled.div`
  position: relative;
  width: 300px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  padding-right: 3rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SearchButton = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #333333;
  }
`;

const AddButton = styled.button`
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #333333;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: none;
  background-color: ${props => props.active ? '#1976D2' : 'transparent'};
  color: ${props => props.active ? 'white' : '#1976D2'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.875rem;
  min-width: 40px;
  text-decoration: ${props => props.active ? 'none' : 'underline'};

  &:hover:not(:disabled) {
    background-color: ${props => props.active ? '#1565C0' : '#E3F2FD'};
  }

  &:disabled {
    color: #9E9E9E;
    text-decoration: none;
    cursor: not-allowed;
  }
`;

const TableContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: #F9FAFB;
  border-bottom: 1px solid #E5E7EB;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #E5E7EB;
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: ${props => props.align || 'center'};
  font-size: 16px;
  font-weight: 400;
  color: #A3A3A3;
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 14px;
  color: #6F6F6F;
  text-align: ${props => props.align || 'center'};
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
`;

const TextButton = styled.button`
  background: none;
  border: none;
  color: #404E67;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const Campaign = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const itemsPerPage = 20;

  // ดึงข้อมูลแคมเปญจาก API
  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Scroll to top เมื่อเปลี่ยนหน้า
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const fetchCampaigns = async (keyword = '') => {
    setLoading(true);
    setError(null);
    try {
      // ส่ง pageNow และ keyword ไปที่ API
      const response = await campaignAPI.getList(currentPage, keyword);

      console.log('🔍 fetchCampaigns - Raw API Response:', response);

      // API ส่งกลับมาในรูปแบบ { result: true, responseText: "ทำรายการสำเร็จ", list: [...] }
      if (response.result && response.list && Array.isArray(response.list)) {
        // Map API fields to frontend fields
        const mappedCampaigns = response.list.map(campaign => {
          console.log('🔍 Campaign from API:', { id: campaign.id, name: campaign.name, day: campaign.day });
          // แปลงวันที่จาก YYYY-MM-DD เป็นรูปแบบที่อ่านง่าย
          const formatDate = (dateStr) => {
            if (!dateStr || dateStr === '0000-00-00' || dateStr === '00/00/0000') return '-';
            try {
              // ถ้าเป็นรูปแบบ YYYY-MM-DD
              if (dateStr.includes('-')) {
                const [year, month, day] = dateStr.split('-');
                return `${day}/${month}/${year}`;
              }
              // ถ้าเป็นรูปแบบอื่นแล้วก็ return ตามเดิม
              return dateStr;
            } catch (e) {
              return dateStr;
            }
          };

          return {
            // เก็บข้อมูลดิบจาก API สำหรับการแก้ไข
            ...campaign,
            // ข้อมูลที่ map สำหรับแสดงในตาราง
            id: campaign.id,
            code: campaign.id, // API ไม่มี code ใช้ id แทน
            company: campaign.companyname || '-',
            name: campaign.name || '-',
            duration: campaign.day ? `${campaign.day} วัน` : '-',
            cost: campaign.price ? `${campaign.price} บาท` : 'ฟรี',
            promoCode: campaign.promocode || '-',
            startDate: formatDate(campaign.datestart),
            endDate: formatDate(campaign.datestop)
          };
        });
        setCampaigns(mappedCampaigns);
        setAllCampaigns(mappedCampaigns);
      } else if (response.data && Array.isArray(response.data)) {
        setCampaigns(response.data);
        setAllCampaigns(response.data);
      } else if (Array.isArray(response)) {
        setCampaigns(response);
        setAllCampaigns(response);
      } else {
        // ถ้า API ส่งรูปแบบไม่ตรง ใช้ mock data
        setCampaigns(campaignData);
        setAllCampaigns(campaignData);
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError(err.message);
      // ถ้า API error ให้ใช้ mock data
      setCampaigns(campaignData);
      setAllCampaigns(campaignData);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1); // รีเซ็ตกลับไปหน้า 1 เมื่อค้นหา

    // ถ้าเลือก "ทั้งหมด" ให้แสดงทั้งหมด
    if (searchTerm === '') {
      setCampaigns(allCampaigns);
    } else {
      // กรองตามชื่อบริษัทที่เลือก
      const filtered = allCampaigns.filter(campaign => campaign.name === searchTerm);
      setCampaigns(filtered);
    }
  };

  const handleAddCampaign = async (campaignData) => {
    try {
      await campaignAPI.add(campaignData);
      fetchCampaigns(); // รีเฟรชข้อมูล
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error adding campaign:', err);
      setError('เกิดข้อผิดพลาดในการเพิ่มบริษัท: ' + err.message);
    }
  };

  const handleUpdateCampaign = async (campaignData) => {
    try {
      const updateResponse = await campaignAPI.update(campaignData);
      console.log('✅ Campaign updated successfully:', updateResponse);

      // รอสักครู่ให้ backend commit ข้อมูล
      await new Promise(resolve => setTimeout(resolve, 500));

      await fetchCampaigns(); // รีเฟรชข้อมูล
      setIsEditModalOpen(false);
      setSelectedCampaign(null);
    } catch (err) {
      console.error('Error updating campaign:', err);
      setError('เกิดข้อผิดพลาดในการแก้ไขบริษัท: ' + err.message);
    }
  };

  const handleEditClick = (campaign) => {
    console.log('📝 Opening edit modal for campaign:', campaign);
    setSelectedCampaign(campaign);
    setIsEditModalOpen(true);
  };

  const totalPages = Math.max(1, Math.ceil((campaigns?.length || 0) / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = campaigns?.slice(startIndex, endIndex) || [];

  return (
    <Layout>
      <PageContainer>
        {/* Loading & Error States */}
        {loading && <div style={{ textAlign: 'center', padding: '1rem' }}>กำลังโหลดข้อมูล...</div>}
        {error && <div style={{ textAlign: 'center', padding: '1rem', color: '#EF4444' }}>เกิดข้อผิดพลาด: {error}</div>}

        {/* Header Section */}
        <HeaderSection>
          {/* Search Dropdown */}
          <SearchContainer>
            <select
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                // อัพเดทผลลัพธ์ทันทีเมื่อเปลี่ยน dropdown
                if (e.target.value === '') {
                  setCampaigns(allCampaigns);
                } else {
                  const filtered = allCampaigns.filter(campaign => campaign.name === e.target.value);
                  setCampaigns(filtered);
                }
              }}
            >
              <option value="">ทั้งหมด</option>
              {allCampaigns
                .filter((campaign) => campaign.name && campaign.name !== '-')
                .map((campaign) => (
                  <option key={campaign.id} value={campaign.name}>
                    {campaign.name}
                  </option>
                ))}
            </select>
          </SearchContainer>

          {/* Add Button */}
          <AddButton onClick={() => setIsModalOpen(true)}>
            เพิ่มบริษัท
          </AddButton>
        </HeaderSection>

        {/* Pagination */}
        <PaginationContainer>
          <PaginationButton
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            previous
          </PaginationButton>
          {[...Array(totalPages)].map((_, index) => (
            <PaginationButton
              key={index + 1}
              active={currentPage === index + 1}
              onClick={() => setCurrentPage(index + 1)}
            >
              {index + 1}
            </PaginationButton>
          ))}
          <PaginationButton
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            next
          </PaginationButton>
        </PaginationContainer>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <tr>
                <TableHeader>ลำดับ</TableHeader>
                <TableHeader>รหัสบริษัท</TableHeader>
                <TableHeader>บริษัท</TableHeader>
                <TableHeader>รายละเอียดแคมเปญ</TableHeader>
                {/* <TableHeader>ระยะเวลาแคมเปญ <br></br>(รายบุคคล)</TableHeader>
                <TableHeader>วันที่เริ่มต้น</TableHeader>
                <TableHeader>วันที่สิ้นสุด</TableHeader> */}
                <TableHeader></TableHeader>
              </tr>
            </TableHead>
            <tbody>
              {currentData.map((campaign, index) => (
                <TableRow key={campaign.id}>
                  <TableCell>{startIndex + index + 1}</TableCell>
                  <TableCell>{campaign.code}</TableCell>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>{campaign.caption || '-'}</TableCell>
                  {/* <TableCell>{campaign.duration}</TableCell>
                  <TableCell>{campaign.startDate}</TableCell>
                  <TableCell>{campaign.endDate}</TableCell> */}
                  <TableCell>
                    <ActionButtonsContainer>
                      <TextButton onClick={() => handleEditClick(campaign)}>แก้ไข</TextButton>
                    </ActionButtonsContainer>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </PageContainer>

      {/* Campaign Management Modal - เพิ่มบริษัทใหม่ */}
      <CampaignManagementModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleAddCampaign}
      />

      {/* Edit Campaign Management Modal - แก้ไขบริษัท */}
      <EditCampaignManagementModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCampaign(null);
        }}
        onSave={handleUpdateCampaign}
        campaignData={selectedCampaign}
      />
    </Layout>
  );
};

export default Campaign;
